// src/components/InternalIssueListView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import IssueCard from './IssueCard';
import CreateEntityModal from './CreateEntityModal';
import { ISSUE_STATUS_OPTIONS, ISSUE_SEVERITY_OPTIONS } from '../constants';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import { ROUTES } from '../constants/routes';
import { DepartmentSelector, usePersistedFilters, DropdownCheckbox, Tooltip, SingleSelectDropdown } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { RiInformationLine, RiAddLine } from 'react-icons/ri';
import { useEntityNavigationContext } from './EntityNavigation';
import { useStaffContext } from '../contexts/StaffContext';

// LocalStorage key for Eisenhower Matrix visibility (shared across views)
const EISENHOWER_VISIBLE_KEY = 'cortex-eisenhower-visible';

const InternalIssueListView = () => {
    const { apiService, departmentId: departmentIdFromContext, dateRange, setDateRange } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    const { getDriDisplayName } = useStaffContext();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);
    
    const [issuesList, setIssuesList] = useState([]);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [errorIssues, setErrorIssues] = useState(null);
    // Use persisted filter for severity (can be set from dashboard card)
    const [issueSeverityFilter, setIssueSeverityFilter] = usePersistedFilters('issueSeverityFilter', '');
    const [issueStatusFilter, setIssueStatusFilter] = useState('');

    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = usePersistedFilters('selectedDris', []);
    
    // Related Clients filter
    const [relatedClients, setRelatedClients] = useState([]);
    const [selectedClients, setSelectedClients] = usePersistedFilters('selectedClients', []);

    // Create modal state
    const [showCreateIssueModal, setShowCreateIssueModal] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [initiativesForCreate] = useState([]);
    
    // Eisenhower Matrix filters - persisted
    const [minImportanceFilter, setMinImportanceFilter] = usePersistedFilters('minImportance', 0);
    const [minUrgencyFilter, setMinUrgencyFilter] = usePersistedFilters('minUrgency', 0);

    // Eisenhower Matrix visibility - persisted to localStorage (shared across views)
    const [showEisenhowerMatrix, setShowEisenhowerMatrix] = useState(() => {
        const saved = localStorage.getItem(EISENHOWER_VISIBLE_KEY);
        return saved === 'true';
    });
    
    const toggleEisenhowerMatrix = () => {
        setShowEisenhowerMatrix(prev => {
            const newState = !prev;
            localStorage.setItem(EISENHOWER_VISIBLE_KEY, String(newState));
            return newState;
        });
    };
    
    const hasActiveFilters = selectedDris.length > 0 || issueSeverityFilter !== '' || issueStatusFilter !== '' || minImportanceFilter > 0 || minUrgencyFilter > 0 || selectedClients.length > 0;
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
        setIssueSeverityFilter('');
        setIssueStatusFilter('');
        setMinImportanceFilter(0);
        setMinUrgencyFilter(0);
        setSelectedClients([]);
    };

    const loadDris = useCallback(async () => {
        if (!apiService || !selectedDepartment) return;
        try {
            const driResult = await apiService.getDepartmentDRIs(selectedDepartment);
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        }
    }, [apiService, selectedDepartment]);

    const loadRelatedClients = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        try {
            const clientResult = await apiService.getDepartmentRelatedClients(selectedDepartment, dateRange.start, dateRange.end);
            setRelatedClients(clientResult.map(r => r.clientName).filter(Boolean));
        } catch (err) {
            console.error('Error loading related clients:', err);
        }
    }, [apiService, selectedDepartment, dateRange]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    useEffect(() => {
        if (selectedDepartment && dateRange && dateRange.start && dateRange.end) {
            loadRelatedClients();
        }
    }, [selectedDepartment, dateRange, loadRelatedClients]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleClientChange = (client) => {
        setSelectedClients(prev =>
            prev.includes(client) ? prev.filter(c => c !== client) : [...prev, client]
        );
    };

    // Load departments
    const loadDepartments = useCallback(async () => {
        if (!apiService) return;
        setLoadingDepartments(true);
        try {
            const deptResult = await apiService.getDepartmentList();
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [apiService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (!apiService) return;
        const loadCreateOptions = async () => {
            try {
                const [staffResult, clientsResult] = await Promise.all([
                    apiService.getAllStaff(false).catch(() => []),
                    apiService.getAllClientsWithStats().catch(() => [])
                ]);
                setStaffList(staffResult.map(s => s.staffName || s.name).filter(Boolean));
                setAllClients(
                    clientsResult
                        .map(c => ({ id: c.clientId || c.name, name: c.name }))
                        .filter(c => c.id && c.name)
                );
            } catch (err) {
                console.error('Error loading create options:', err);
            }
        };
        loadCreateOptions();
    }, [apiService]);

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const handleDepartmentChange = (newDept) => {
        navigate(`${ROUTES.OPERATIONS}/${newDept}/issues`);
    };

    const loadDepartmentIssues = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        
        setLoadingIssues(true);
        setErrorIssues(null);
        setIssuesList([]);

        try {
            const issues = await apiService.getDepartmentIssuesList(
                selectedDepartment,
                dateRange.start,
                dateRange.end,
                issueSeverityFilter || null,
                issueStatusFilter || null,
                selectedDris.length > 0 ? selectedDris : null,
                selectedClients.length > 0 ? selectedClients : null
            );
            setIssuesList(issues);
        } catch (err) {
            console.error('Error loading department issues:', err);
            setErrorIssues(err.message);
        } finally {
            setLoadingIssues(false);
        }
    }, [apiService, selectedDepartment, dateRange, issueSeverityFilter, issueStatusFilter, selectedDris, selectedClients]);

    useEffect(() => {
        if (selectedDepartment && dateRange && dateRange.start && dateRange.end) {
            loadDepartmentIssues();
        }
    }, [loadDepartmentIssues, selectedDepartment, dateRange, selectedDris, issueSeverityFilter, issueStatusFilter]);

    // Filter issues by importance and urgency
    const filteredIssues = useMemo(() => {
        return issuesList.filter(issue =>
            (issue.importanceScore || 0) >= minImportanceFilter &&
            (issue.urgencyScore || 0) >= minUrgencyFilter
        );
    }, [issuesList, minImportanceFilter, minUrgencyFilter]);

    // Handle issue status update
    const handleIssueStatusChange = useCallback(async (issueId, newStatus) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeStatus(issueId, newStatus);
            setIssuesList(prevList =>
                prevList.map(issue =>
                    issue.id === issueId ? { ...issue, status: newStatus } : issue
                )
            );
        } catch (err) {
            console.error('Error updating issue status:', err);
        }
    }, [apiService]);

    // Handle issue severity update
    const handleIssueSeverityChange = useCallback(async (issueId, newSeverity) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeProperty(issueId, 'severity', newSeverity);
            setIssuesList(prevList =>
                prevList.map(issue =>
                    issue.id === issueId ? { ...issue, severity: newSeverity } : issue
                )
            );
        } catch (err) {
            console.error('Error updating issue severity:', err);
        }
    }, [apiService]);

    const handleIssueCreated = (newIssue) => {
        if (newIssue) {
            // Format the issue with expected fields
            const formattedIssue = {
                ...newIssue,
                assigned_to: newIssue.assigned_to || newIssue.assignee,
                importanceScore: 0,
                urgencyScore: 1.5,
            };
            setIssuesList(prev => [formattedIssue, ...prev]);
        } else {
            loadDepartmentIssues();
        }
        setShowCreateIssueModal(false);
    };

    // Custom API service wrapper for creating department issues (when "Internal" is selected)
    const departmentApiService = useMemo(() => {
        if (!apiService || !selectedDepartment) return null;
        return new Proxy(apiService, {
            get(target, prop) {
                if (prop === 'createIssue') {
                    return (clientId, data) => target.createDepartmentIssue(selectedDepartment, data);
                }
                const value = target[prop];
                if (typeof value === 'function') {
                    return value.bind(target);
                }
                return value;
            }
        });
    }, [apiService, selectedDepartment]);

    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} driDisplayFormatter={getDriDisplayName}
                filterGroups={[
                    { id: 'context', label: 'Context', filters: [{ key: 'department' }, { key: 'client' }] },
                    { id: 'issues', label: 'Issues', filters: [{ key: 'severity' }, { key: 'issueStatus' }] }
                ]}
                filters={{ department: selectedDepartment, client: selectedClients, severity: issueSeverityFilter, issueStatus: issueStatusFilter }}
                filterConfigs={{
                    department: { label: 'Department', render: () => departments.length > 0 && <DepartmentSelector departments={departments} selectedDepartment={selectedDepartment} onDepartmentChange={handleDepartmentChange} loading={loadingDepartments} /> },
                    client: { label: 'Client', render: () => <DropdownCheckbox options={relatedClients} selectedOptions={selectedClients} onChange={handleClientChange} title="Client" /> },
                    severity: { label: 'Severity', render: () => <SingleSelectDropdown id="severityFilter" label="Severity" value={issueSeverityFilter} onChange={setIssueSeverityFilter} options={ISSUE_SEVERITY_OPTIONS} allLabel="All" /> },
                    issueStatus: { label: 'Issue Status', render: () => <SingleSelectDropdown id="issueStatusFilter" label="Issue Status" value={issueStatusFilter} onChange={setIssueStatusFilter} options={ISSUE_STATUS_OPTIONS} allLabel="All" /> }
                }}
                activeFilterChips={[
                    ...(selectedClients.length > 0 ? [{ label: 'Clients', value: selectedClients.length === 1 ? selectedClients[0] : `${selectedClients.length} selected`, onRemove: () => setSelectedClients([]) }] : []),
                    ...(issueSeverityFilter ? [{ label: 'Severity', value: issueSeverityFilter.charAt(0).toUpperCase() + issueSeverityFilter.slice(1), onRemove: () => setIssueSeverityFilter('') }] : []),
                    ...(issueStatusFilter ? [{ label: 'Status', value: issueStatusFilter.charAt(0).toUpperCase() + issueStatusFilter.slice(1), onRemove: () => setIssueStatusFilter('') }] : []),
                ]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />
            
            {/* Eisenhower Matrix Section - Collapsible */}
            <div className="mb-8 bg-white rounded-lg shadow overflow-hidden">
                <button
                    onClick={toggleEisenhowerMatrix}
                    className="w-full px-4 py-3 md:px-6 md:py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200 border-b border-gray-200"
                >
                    <div className="flex items-center">
                        <svg 
                            className={`h-5 w-5 text-gray-500 mr-3 transition-transform duration-200 ${showEisenhowerMatrix ? 'rotate-90' : ''}`}
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                        >
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Priority Matrix</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Issues prioritized by urgency and importance</p>
                        </div>
                    </div>
                </button>
                
                {showEisenhowerMatrix && (
                    <div className="p-4 md:p-6">
                        <EisenhowerMatrixView 
                            departmentId={selectedDepartment} 
                            clientId={null}
                            typeFilter={['Issue']}
                            driFilter={selectedDris}
                            clientFilter={selectedClients}
                            minImportanceFilter={minImportanceFilter}
                            minUrgencyFilter={minUrgencyFilter}
                            issueSeverityFilter={issueSeverityFilter}
                            issueStatusFilter={issueStatusFilter}
                        />
                    </div>
                )}
            </div>
            {/* Issues Card Grid */}
            <div className="rounded-xl shadow p-4 md:p-6" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>Issues</h3>
                        <Tooltip content="Issues are shown if they have a related communication within the selected date range, or if they block an initiative that has a communication in the range.">
                            <RiInformationLine className="w-4 h-4 cursor-help" style={{ color: 'var(--pallium-icon-gray)' }} />
                        </Tooltip>
                    </div>
                    <button
                        onClick={() => setShowCreateIssueModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                        style={{ 
                            backgroundColor: 'rgba(252, 57, 57, 0.1)',
                            color: 'var(--pallium-error-red)',
                        }}
                    >
                        <RiAddLine className="w-4 h-4" />
                        New Issue
                    </button>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Issues related to {selectedDepartment || 'this department'}
                </p>
                
                {loadingIssues ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>Loading issues...</div>
                ) : errorIssues ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-error-red)' }}>Error: {errorIssues}</div>
                ) : filteredIssues.length === 0 ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>No issues found matching the current filters.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredIssues.map((issue, index) => (
                            <IssueCard
                                key={issue.id || index}
                                issue={{
                                    ...issue,
                                    issueName: issue.name,
                                    issueId: issue.id,
                                    dri: issue.assigned_to,
                                    riskScore: issue.score,
                                    blockedInitiatives: Array.isArray(issue.blockedInitiatives) ? issue.blockedInitiatives.length : issue.blockedInitiatives
                                }}
                                clientId={null}
                                onClick={() => navigate(`${ROUTES.OPERATIONS}/${selectedDepartment}/issues/${issue.id}`)}
                                editable={true}
                                statusOptions={ISSUE_STATUS_OPTIONS}
                                severityOptions={ISSUE_SEVERITY_OPTIONS}
                                onStatusChange={handleIssueStatusChange}
                                onSeverityChange={handleIssueSeverityChange}
                                data-testid="issue-card"
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Create Issue Modal - Single step with optional account selection */}
            {showCreateIssueModal && (
                <CreateEntityModal
                    isOpen={true}
                    onClose={() => setShowCreateIssueModal(false)}
                    entityType="issue"
                    clientId={null}
                    apiService={apiService}
                    onSuccess={handleIssueCreated}
                    staffList={staffList}
                    initiativesList={initiativesForCreate}
                    clientsList={allClients}
                    departmentId={selectedDepartment}
                    departmentApiService={departmentApiService}
                />
            )}
        </>
    );
};

export default InternalIssueListView;